import { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useLocation } from 'react-router-dom';

const PEARL = new THREE.Color('#E2E2E2');
const JADE = new THREE.Color('#9EAC99');
const SKY = new THREE.Color('#AEBFCB');

const supportsWebGL = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
  } catch {
    return false;
  }
};

const Fabric = ({ compact }) => {
  const mesh = useRef();
  const material = useRef();
  const progress = useRef(0);
  const { geometry, base } = useMemo(() => {
    const shape = new THREE.PlaneGeometry(compact ? 3 : 3.8, compact ? 4.4 : 5.4, compact ? 28 : 52, compact ? 42 : 76);
    return { geometry: shape, base: Float32Array.from(shape.attributes.position.array) };
  }, [compact]);

  useEffect(() => {
    const update = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      progress.current = Math.min(1, Math.max(0, window.scrollY / max));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      geometry.dispose();
    };
  }, [geometry]);

  useFrame(({ clock }) => {
    if (!mesh.current) return;
    const elapsed = clock.elapsedTime;
    const p = progress.current;
    const positions = mesh.current.geometry.attributes.position;
    for (let index = 0; index < positions.array.length; index += 3) {
      const x = base[index];
      const y = base[index + 1];
      positions.array[index + 2] =
        Math.sin(x * (2.2 + p) + elapsed * 0.65) * 0.34 +
        Math.cos(y * 1.7 - elapsed * 0.45) * (0.12 + p * 0.2);
      positions.array[index] = x + Math.sin(y * 1.25 + elapsed * 0.35) * (0.08 + p * 0.16);
    }
    positions.needsUpdate = true;
    mesh.current.geometry.computeVertexNormals();
    mesh.current.rotation.y = -0.35 + p * Math.PI * 1.35;
    mesh.current.rotation.x = 0.12 + p * 0.32;
    mesh.current.position.x = (compact ? 0.65 : 1.45) - p * (compact ? 1.3 : 2.1);
    mesh.current.position.y = Math.sin(elapsed * 0.45) * 0.12 - p * 0.25;
    const scale = 1 + Math.sin(p * Math.PI) * 0.28;
    mesh.current.scale.setScalar(scale);
    if (material.current) {
      material.current.color.copy(PEARL).lerp(JADE, Math.min(p * 1.45, 1)).lerp(SKY, Math.max((p - 0.72) * 2.1, 0));
    }
  });

  return (
    <mesh ref={mesh} geometry={geometry} rotation={[0.12, -0.35, -0.18]}>
      <meshPhysicalMaterial
        ref={material}
        color="#E2E2E2"
        metalness={0.34}
        roughness={0.42}
        clearcoat={1}
        clearcoatRoughness={0.26}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
};

export const RunwaySculpture = () => (
  <GlobalFabricBackground />
);

const GlobalFabricBackground = () => {
  const { pathname } = useLocation();
  const [mode, setMode] = useState('checking');
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const narrow = window.matchMedia('(max-width: 640px)');
    const update = () => {
      setCompact(narrow.matches);
      setMode(reduced.matches || !supportsWebGL() ? 'fallback' : 'webgl');
    };
    update();
    reduced.addEventListener?.('change', update);
    narrow.addEventListener?.('change', update);
    return () => {
      reduced.removeEventListener?.('change', update);
      narrow.removeEventListener?.('change', update);
    };
  }, []);
  if (pathname === '/v2') return null;
  return (
    <div className={`global-runway-background ${mode === 'fallback' ? 'global-runway-fallback' : ''}`} data-testid="global-runway-3d" aria-hidden="true">
      {mode === 'webgl' && (
        <Canvas camera={{ position: [0, 0, compact ? 6.6 : 6], fov: compact ? 44 : 40 }} dpr={compact ? 1 : [1, 1.5]} gl={{ alpha: true, antialias: !compact, powerPreference: 'high-performance' }}>
          <ambientLight intensity={2.2} />
          <directionalLight position={[4, 5, 5]} intensity={4.5} color="#FFF9E8" />
          <pointLight position={[-4, -1, 3]} intensity={10} color="#C7D1C3" />
          <pointLight position={[3, -3, 2]} intensity={8} color="#B9CAD6" />
          <Fabric compact={compact} />
        </Canvas>
      )}
    </div>
  );
};